import { useState, useEffect, useRef, useCallback } from 'react';
import { useLottery, LotteryItem } from '../../../hooks/useLottery';

interface Props { onClose: () => void; }
type Phase = 'idle' | 'spinning' | 'result';
type SpinStage = 1 | 2 | 3;

// ── Audio helpers ─────────────────────────────────────────────────────────────
let _audioCtx: AudioContext | null = null;
function getACtx(): AudioContext | null {
  try {
    if (!_audioCtx || _audioCtx.state === 'closed') _audioCtx = new AudioContext();
    if (_audioCtx.state === 'suspended') _audioCtx.resume();
    return _audioCtx;
  } catch { return null; }
}
function playBeep(freq: number, dur: number, vol: number, type: 'square'|'sine'|'triangle'|'sawtooth' = 'square', delay = 0) {
  const ctx = getACtx(); if (!ctx) return;
  try {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.connect(g); g.connect(ctx.destination);
    osc.type = type; osc.frequency.value = freq;
    const t = ctx.currentTime + delay;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.start(t); osc.stop(t + dur + 0.02);
  } catch { /* ignore */ }
}
function playTick(speed: number) {
  const freq = speed < 80 ? 900 : speed < 150 ? 680 : speed < 350 ? 500 : 380;
  const vol  = speed < 80 ? 0.035 : speed < 150 ? 0.055 : 0.09;
  playBeep(freq, 0.055, vol, 'square');
}
function playFanfare() {
  // 衝擊鼓
  playBeep(70, 0.5, 0.3, 'triangle', 0);
  // 上行音階
  [523, 659, 784, 1047, 1319].forEach((f, i) => playBeep(f, 0.4, 0.13, 'sine', i * 0.09));
  // 和聲
  [659, 987].forEach((f, i) => playBeep(f, 0.6, 0.07, 'sine', 0.4 + i * 0.05));
}

// ── Particle system ───────────────────────────────────────────────────────────
interface Particle { id: number; dx: number; dy: number; color: string; size: number; delay: number; dur: number; rot: number; shape: 'circle'|'diamond'|'spark'; }
const P_COLORS = ['#d4a843','#f0e060','#e05555','#7ec8a0','#ffffff','#a78bfa','#ff8800','#ffd700','#ff4444','#44ddff'];
function genParticles(n: number): Particle[] {
  return Array.from({ length: n }, (_, i) => {
    const a = (Math.random() * 360 * Math.PI) / 180;
    const spd = 70 + Math.random() * 220;
    return {
      id: i,
      dx: Math.cos(a) * spd,
      dy: Math.sin(a) * spd,
      color: P_COLORS[i % P_COLORS.length],
      size: 3 + Math.random() * 8,
      delay: Math.random() * 0.18,
      dur: 0.75 + Math.random() * 0.65,
      rot: Math.random() * 720,
      shape: (['circle','diamond','spark'] as const)[Math.floor(Math.random() * 3)],
    };
  });
}

// ── Floating sparkles (result phase) ─────────────────────────────────────────
const SPARKLES = Array.from({ length: 22 }, (_, i) => ({
  id: i,
  left: `${4 + ((i * 43 + 7) % 92)}%`,
  size: 3 + (i % 3) * 1.5,
  delay: `${(i * 0.17) % 2.4}s`,
  dur: `${1.6 + (i % 4) * 0.5}s`,
  color: ['#d4a843','#fff7a0','#ffd700','#e8c050','#ffaa00'][i % 5],
  drift: `${((i % 7) - 3) * 22}px`,
}));

const MAX_SPINS = 20;
const FONT_DECO = "'Cinzel', serif";
const FONT_HUD  = "'Orbitron', monospace";

export default function LotteryModal({ onClose }: Props) {
  const { items, loading, drawRandom } = useLottery();
  const [phase, setPhase]           = useState<Phase>('idle');
  const [spinStage, setSpinStage]   = useState<SpinStage>(1);
  const [displayText, setDisplayText] = useState('');
  const [result, setResult]         = useState<LotteryItem | null>(null);
  const [spinCount, setSpinCount]   = useState(0);
  const [particles, setParticles]   = useState<Particle[]>([]);
  const [flashBg, setFlashBg]       = useState(false);
  const [tickBgColor, setTickBgColor] = useState('');
  const [showResult, setShowResult] = useState(false);
  const spinRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countRef = useRef(0);

  const isEmpty = !loading && items.length === 0;

  const handleDraw = useCallback(() => {
    if (phase !== 'idle' || items.length === 0) return;
    setPhase('spinning');
    setSpinStage(1);
    setResult(null);
    setShowResult(false);
    setParticles([]);
    countRef.current = 0;
    setSpinCount(0);

    const chosen = drawRandom();
    let speed = 48;

    const spin = () => {
      const c = ++countRef.current;
      setSpinCount(c);

      const r = items[Math.floor(Math.random() * items.length)];
      setDisplayText(r?.content ?? '');

      // Flash tint per tick
      const col = P_COLORS[c % P_COLORS.length];
      setTickBgColor(col);
      setTimeout(() => setTickBgColor(''), Math.min(speed * 0.38, 55));

      playTick(speed);

      // Stage transitions
      if (c < 10) {
        setSpinStage(1);
        speed = 48 + c * 2;
      } else if (c < 16) {
        setSpinStage(2);
        speed = 75 + (c - 10) * 20;
      } else {
        setSpinStage(3);
        speed = 220 + (c - 16) * 90;
      }

      if (c < MAX_SPINS) {
        spinRef.current = setTimeout(spin, speed);
      } else {
        // Final land
        setDisplayText(chosen?.content ?? '');
        setTimeout(() => {
          setResult(chosen);
          setPhase('result');
          setShowResult(true);
          setParticles(genParticles(80));
          setFlashBg(true);
          playFanfare();
          setTimeout(() => setFlashBg(false), 400);
          setTimeout(() => setParticles([]), 1400);
        }, 300);
      }
    };

    spinRef.current = setTimeout(spin, speed);
  }, [phase, items, drawRandom]);

  useEffect(() => () => { if (spinRef.current) clearTimeout(spinRef.current); }, []);

  const stageLabel = spinStage === 1 ? 'SPINNING' : spinStage === 2 ? 'SLOWING' : '!! CRITICAL !!';
  const stageColor = spinStage === 1 ? '#7ec8a0' : spinStage === 2 ? '#f0a86e' : '#e05555';
  const progress   = Math.min((spinCount / MAX_SPINS) * 100, 100);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">

      {/* ── Screen flash on result ── */}
      {flashBg && (
        <div className="absolute inset-0 z-[60] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at center, rgba(212,168,67,0.75) 0%, rgba(255,120,0,0.25) 45%, transparent 75%)', animation: 'lFlash 0.4s ease-out forwards' }} />
      )}

      {/* ── Backdrop ── */}
      <div className="absolute inset-0 backdrop-blur-md"
        style={{ background: 'rgba(0,0,0,0.93)' }}
        onClick={phase === 'idle' || phase === 'result' ? onClose : undefined} />

      {/* ── Modal ── */}
      <div
        className="relative w-full max-w-sm z-10 rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(160deg,#0f0f0f 0%,#0a0a0a 55%,#0d0900 100%)',
          border: `1.5px solid ${phase === 'result' ? '#d4a84370' : '#d4a84330'}`,
          boxShadow: phase === 'result'
            ? '0 0 0 1px #d4a84320, 0 0 80px rgba(212,168,67,0.5), 0 32px 80px rgba(0,0,0,0.95)'
            : '0 0 0 1px #d4a84310, 0 0 40px rgba(212,168,67,0.1), 0 32px 80px rgba(0,0,0,0.95)',
          animation: (phase === 'spinning' && spinStage === 3) ? 'lShake 0.08s ease-in-out infinite' : 'none',
          transition: 'box-shadow 0.8s',
        }}
      >
        {/* Top scan beam */}
        <div className="absolute top-0 left-0 right-0 h-px overflow-hidden z-10">
          <div className="h-full" style={{ background: 'linear-gradient(90deg,transparent,#d4a84390,transparent)', animation: 'lScan 2.5s linear infinite' }} />
        </div>

        {/* Corner brackets */}
        {[['top-2 left-2','border-l border-t'],['top-2 right-2','border-r border-t'],['bottom-2 left-2','border-l border-b'],['bottom-2 right-2','border-r border-b']].map(([pos,brd]) => (
          <div key={pos} className={`absolute ${pos} w-4 h-4 pointer-events-none ${brd}`} style={{ borderColor: '#d4a84340' }} />
        ))}

        {/* ── Particles ── */}
        {particles.map(p => (
          <div key={p.id} className="absolute pointer-events-none z-20"
            style={{
              left: '50%', top: '42%',
              width: p.size, height: p.size,
              background: p.color,
              borderRadius: p.shape === 'circle' ? '50%' : p.shape === 'diamond' ? '2px' : '1px',
              transform: p.shape === 'diamond' ? 'translate(-50%,-50%) rotate(45deg)' : 'translate(-50%,-50%)',
              boxShadow: `0 0 ${p.size * 2.5}px ${p.color}`,
              '--dx': `${p.dx}px`,
              '--dy': `${p.dy}px`,
              '--rot': `${p.rot}deg`,
              animation: `lPart ${p.dur}s ${p.delay}s cubic-bezier(0.15,0.8,0.3,1) forwards`,
            } as React.CSSProperties}
          />
        ))}

        {/* ── Shockwave rings (result) ── */}
        {showResult && [0, 110, 220].map((delay, i) => (
          <div key={i} className="absolute pointer-events-none z-20 rounded-full"
            style={{
              left: 'calc(50% - 5px)', top: 'calc(42% - 5px)',
              width: 10, height: 10,
              border: `${2 - i * 0.5}px solid rgba(212,168,67,${0.9 - i * 0.25})`,
              animation: `lRing 0.72s ${delay}ms ease-out forwards`,
            }}
          />
        ))}

        {/* ── Floating sparkles (result phase) ── */}
        {phase === 'result' && SPARKLES.map(s => (
          <div key={s.id} className="absolute pointer-events-none z-10 rounded-full"
            style={{
              left: s.left, bottom: 0,
              width: s.size, height: s.size,
              background: s.color,
              boxShadow: `0 0 ${s.size * 2}px ${s.color}`,
              '--drift': s.drift,
              animation: `lSparkle ${s.dur} ${s.delay} ease-in-out infinite`,
              opacity: 0,
            } as React.CSSProperties}
          />
        ))}

        {/* ── Header ── */}
        <div className="px-5 py-4 flex items-center justify-between"
          style={{ borderBottom: '1px solid #d4a84320', background: 'linear-gradient(90deg,#d4a84314,transparent 60%)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg"
              style={{ background: '#d4a84318', border: '1px solid #d4a84445', boxShadow: '0 0 14px rgba(212,168,67,0.3)' }}>
              <i className="ri-lottery-line text-[#d4a843] text-base" />
            </div>
            <div>
              <h3 className="text-white text-sm font-black tracking-widest" style={{ fontFamily: FONT_DECO }}>命運抽籤</h3>
              <p className="text-[10px] mt-0.5" style={{ color: '#d4a84380' }}>
                {loading ? '載入中…' : `共 ${items.length} 支籤`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {phase === 'spinning' && (
              <span className="text-[9px] font-black tracking-[0.18em] px-2 py-1 rounded-sm"
                style={{
                  background: `${stageColor}18`,
                  color: stageColor,
                  border: `1px solid ${stageColor}40`,
                  animation: spinStage === 3 ? 'lBlink 0.35s ease-in-out infinite' : 'none',
                  fontFamily: FONT_HUD,
                }}>
                {stageLabel}
              </span>
            )}
            {(phase === 'idle' || phase === 'result') && (
              <button onClick={onClose}
                className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-all"
                style={{ color: '#555' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#d4a843'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#555'; }}>
                <i className="ri-close-line text-lg" />
              </button>
            )}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="px-5 py-6">
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <div className="w-16 h-16 flex items-center justify-center rounded-full"
                style={{ background: '#d4a84312', border: '1.5px dashed #d4a84335' }}>
                <i className="ri-inbox-line text-2xl" style={{ color: '#d4a84360' }} />
              </div>
              <div className="text-center">
                <p className="text-[#444] text-sm font-black tracking-widest uppercase">籤池是空的</p>
                <p className="text-[#2a2a2a] text-xs mt-1.5">請裁判先在面板中新增籤的內容</p>
              </div>
            </div>
          ) : (
            <>
              {/* ── Display box ── */}
              <div className="relative rounded-xl overflow-hidden flex items-center justify-center mb-5"
                style={{
                  minHeight: phase === 'idle' ? 160 : 130,
                  background: phase === 'result'
                    ? 'linear-gradient(135deg,#d4a84320,#d4a84308)'
                    : phase === 'spinning' && tickBgColor
                    ? `linear-gradient(135deg, ${tickBgColor}22, #0f0f0f)`
                    : '#0f0f0f',
                  border: `1.5px solid ${phase === 'result' ? '#d4a84360' : phase === 'spinning' && spinStage === 3 ? '#e0555550' : '#1a1a1a'}`,
                  boxShadow: phase === 'result'
                    ? '0 0 35px rgba(212,168,67,0.3), inset 0 0 30px rgba(212,168,67,0.07)'
                    : phase === 'spinning' && spinStage === 3
                    ? '0 0 25px rgba(224,85,85,0.25), inset 0 0 15px rgba(224,85,85,0.05)'
                    : 'none',
                  transition: 'background 0.04s, border-color 0.25s, box-shadow 0.25s',
                }}>

                {/* Scanlines (spinning) */}
                {phase === 'spinning' && (
                  <div className="absolute inset-0 pointer-events-none"
                    style={{ backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(255,255,255,0.016) 3px,rgba(255,255,255,0.016) 4px)' }} />
                )}

                {/* Progress bar — top edge */}
                {phase === 'spinning' && (
                  <div className="absolute top-0 left-0 right-0 h-1 overflow-hidden rounded-t-xl z-10">
                    <div style={{
                      height: '100%',
                      width: `${progress}%`,
                      background: spinStage === 1
                        ? 'linear-gradient(90deg,#7ec8a0,#7ec8a0bb)'
                        : spinStage === 2
                        ? 'linear-gradient(90deg,#f0a86e,#f0a86ebb)'
                        : 'linear-gradient(90deg,#e05555,#ff8800,#e05555)',
                      boxShadow: spinStage === 3 ? '0 0 10px rgba(224,85,85,0.9)' : 'none',
                      animation: spinStage === 3 ? 'lBarPulse 0.28s ease-in-out infinite' : 'none',
                      transition: 'width 0.08s linear',
                    }} />
                  </div>
                )}

                {/* ── IDLE ── */}
                {phase === 'idle' && (
                  <div className="w-full">
                    <div className="relative w-full h-40 overflow-hidden">
                      <img
                        src="https://public.readdy.ai/ai/img_res/af0b30f44f8f32e6948a05f8d674904b.jpg"
                        alt="命運抽籤"
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom,rgba(15,15,15,0.1) 0%,transparent 40%,rgba(10,10,10,0.85) 100%)' }} />
                      <div className="absolute top-0 left-0 right-0 h-px overflow-hidden">
                        <div className="h-full" style={{ background: 'linear-gradient(90deg,transparent,#d4a84370,transparent)', animation: 'lScan 3s linear infinite' }} />
                      </div>
                      <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-1.5">
                        {[0, 280, 560].map(d => (
                          <div key={d} className="w-1 h-1 rounded-full bg-[#d4a843] opacity-60"
                            style={{ animation: `lDot 1.2s ${d}ms ease-in-out infinite` }} />
                        ))}
                        <p className="text-[#d4a84399] text-[10px] tracking-[0.35em] uppercase font-bold mx-1.5">等待抽籤</p>
                        {[0, 280, 560].map(d => (
                          <div key={d} className="w-1 h-1 rounded-full bg-[#d4a843] opacity-60"
                            style={{ animation: `lDot 1.2s ${d + 140}ms ease-in-out infinite` }} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── SPINNING ── */}
                {phase === 'spinning' && (
                  <div className="text-center px-6 py-5 w-full">
                    {/* Main display text */}
                    <div
                      style={{
                        fontFamily: 'Georgia, serif',
                        fontSize: '1.25rem',
                        fontWeight: 900,
                        letterSpacing: '0.06em',
                        textAlign: 'center',
                        color: spinStage === 3 ? '#e05555' : '#d4a843cc',
                        textShadow: spinStage === 3
                          ? '0 0 28px rgba(224,85,85,0.85), 0 0 55px rgba(224,85,85,0.4)'
                          : '0 0 22px rgba(212,168,67,0.65)',
                        animation: spinStage === 1
                          ? 'lSpinFast 0.07s ease-in-out infinite'
                          : spinStage === 2
                          ? 'lSpinMed 0.14s ease-in-out infinite'
                          : 'lSpinCrit 0.28s ease-in-out infinite',
                      }}
                    >
                      {displayText}
                    </div>

                    {/* Stage indicator dots */}
                    <div className="flex justify-center items-center gap-2 mt-4">
                      {([1,2,3] as SpinStage[]).map(s => (
                        <div key={s}
                          className="rounded-full transition-all duration-200"
                          style={{
                            width: s === spinStage ? 22 : 8,
                            height: 8,
                            background: s <= spinStage
                              ? (s === 1 ? '#7ec8a0' : s === 2 ? '#f0a86e' : '#e05555')
                              : '#1e1e1e',
                            boxShadow: s === spinStage
                              ? `0 0 10px ${s===1?'rgba(126,200,160,0.8)':s===2?'rgba(240,168,110,0.8)':'rgba(224,85,85,0.9)'}`
                              : 'none',
                          }} />
                      ))}
                    </div>

                    {/* Spin count */}
                    <p className="mt-2 text-center"
                      style={{ fontFamily: FONT_HUD, fontSize: '9px', color: spinStage === 3 ? 'rgba(224,85,85,0.7)' : 'rgba(212,168,67,0.35)', letterSpacing: '0.2em' }}>
                      {spinStage === 3 ? '命運即將揭曉' : `${spinCount} / ${MAX_SPINS}`}
                    </p>
                  </div>
                )}

                {/* ── RESULT ── */}
                {phase === 'result' && result && (
                  <div className="text-center px-6 py-6" style={{ animation: 'lResultPop 0.65s cubic-bezier(0.34,1.56,0.64,1)' }}>
                    {/* Crown icon */}
                    <div className="relative w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                      <div className="absolute inset-0 rounded-full"
                        style={{ border: '1.5px solid #d4a84345', animation: 'lRingPulse 1.6s ease-out infinite' }} />
                      <div className="absolute inset-0 rounded-full"
                        style={{ border: '1px solid #d4a84320', animation: 'lRingPulse 1.6s 0.5s ease-out infinite' }} />
                      <div className="w-full h-full rounded-full flex items-center justify-center"
                        style={{ background: 'radial-gradient(circle,#d4a84330,#d4a84308)', border: '1.5px solid #d4a84365', boxShadow: '0 0 28px rgba(212,168,67,0.5), inset 0 0 18px rgba(212,168,67,0.12)' }}>
                        <i className="ri-vip-crown-2-fill text-3xl"
                          style={{ color: '#d4a843', filter: 'drop-shadow(0 0 12px rgba(212,168,67,0.95))', animation: 'lCrownDrop 0.55s cubic-bezier(0.34,1.56,0.64,1)' }} />
                      </div>
                    </div>

                    {/* Label */}
                    <div className="flex items-center justify-center gap-1.5 mb-3">
                      <i className="ri-sparkling-2-fill text-xs" style={{ color: '#d4a843', animation: 'lSpkIcon 0.7s ease-in-out infinite' }} />
                      <span className="text-[#d4a843] text-[10px] font-black tracking-[0.35em] uppercase" style={{ fontFamily: FONT_DECO }}>命運已定</span>
                      <i className="ri-sparkling-2-fill text-xs" style={{ color: '#d4a843', animation: 'lSpkIcon 0.7s 0.18s ease-in-out infinite' }} />
                    </div>

                    {/* Result text */}
                    <div className="text-2xl font-black text-center leading-snug"
                      style={{ color: '#fff', fontFamily: 'Georgia, serif', textShadow: '0 0 35px rgba(212,168,67,0.85), 0 0 70px rgba(212,168,67,0.3)' }}>
                      {result.content}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Action buttons ── */}
              {phase === 'idle' && (
                <button onClick={handleDraw}
                  className="relative w-full py-4 rounded-xl font-black text-sm tracking-[0.22em] uppercase overflow-hidden cursor-pointer whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg,#d4a843,#c89030)', color: '#0d0d0d', boxShadow: '0 0 28px rgba(212,168,67,0.55), 0 4px 12px rgba(212,168,67,0.4)', border: '1px solid #d4a843' }}>
                  <div className="absolute inset-0 opacity-35 pointer-events-none"
                    style={{ background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.5),transparent)', animation: 'lShim 2s linear infinite' }} />
                  <span className="relative flex items-center justify-center gap-2">
                    <i className="ri-shuffle-line text-base" />
                    抽 籤 ！
                  </span>
                </button>
              )}

              {phase === 'spinning' && (
                <div className="relative w-full py-4 rounded-xl text-center text-sm font-black tracking-widest uppercase overflow-hidden"
                  style={{
                    background: spinStage === 3 ? 'linear-gradient(135deg,#1a0000,#0d0000)' : '#111',
                    color: spinStage === 3 ? '#e05555' : '#2a2a2a',
                    border: `1px solid ${spinStage === 3 ? '#e0555535' : '#1e1e1e'}`,
                    fontFamily: FONT_HUD, fontSize: '11px',
                  }}>
                  {spinStage === 3 && (
                    <div className="absolute inset-0 pointer-events-none"
                      style={{ background: 'linear-gradient(90deg,transparent,rgba(224,85,85,0.1),transparent)', animation: 'lShim 0.7s linear infinite' }} />
                  )}
                  <span className="relative">
                    {spinStage === 3
                      ? <><i className="ri-alert-fill mr-1.5 animate-pulse" />命運即將揭曉…</>
                      : <><i className="ri-loader-4-line animate-spin mr-2" />命運流轉中…</>
                    }
                  </span>
                </div>
              )}

              {phase === 'result' && (
                <button onClick={onClose}
                  className="w-full py-3 rounded-xl text-sm font-black cursor-pointer whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg,#d4a843,#c89030)', color: '#0d0d0d', boxShadow: '0 0 20px rgba(212,168,67,0.4)' }}>
                  <i className="ri-check-line mr-1.5" />接受命運
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        /* ── Scan & basics ── */
        @keyframes lScan { from{transform:translateX(-100%)} to{transform:translateX(100%)} }
        @keyframes lDot  { 0%,100%{transform:scale(1);opacity:0.5} 50%{transform:scale(1.7);opacity:1} }
        @keyframes lShim { from{transform:translateX(-200%)} to{transform:translateX(200%)} }
        @keyframes lBlink { 0%,100%{opacity:1} 50%{opacity:0.25} }
        @keyframes lFlash { 0%{opacity:1} 100%{opacity:0} }

        /* ── Spin text animations ── */
        @keyframes lSpinFast {
          0%,100%{opacity:1;transform:scaleY(1)}
          50%{opacity:0.6;transform:scaleY(0.85)}
        }
        @keyframes lSpinMed {
          0%,100%{opacity:1;transform:scaleY(1) scale(1)}
          50%{opacity:0.75;transform:scaleY(0.9) scale(0.97)}
        }
        @keyframes lSpinCrit {
          0%,100%{opacity:1;transform:scale(1);letter-spacing:0.06em}
          40%{opacity:0.85;transform:scale(1.05);letter-spacing:0.12em}
        }

        /* ── Modal shake ── */
        @keyframes lShake {
          0%,100%{transform:translate(0,0)}
          20%{transform:translate(-3px,1px)}
          40%{transform:translate(3px,-1px)}
          60%{transform:translate(-2px,2px)}
          80%{transform:translate(2px,-1px)}
        }

        /* ── Progress bar pulse ── */
        @keyframes lBarPulse {
          0%,100%{opacity:1;filter:brightness(1)}
          50%{opacity:0.7;filter:brightness(1.6)}
        }

        /* ── Result pop ── */
        @keyframes lResultPop {
          from{opacity:0;transform:scale(0.7) translateY(12px)}
          to{opacity:1;transform:scale(1) translateY(0)}
        }

        /* ── Shockwave rings ── */
        @keyframes lRing {
          from{transform:scale(1);opacity:1}
          to{transform:scale(32);opacity:0}
        }

        /* ── Ring pulse on icon ── */
        @keyframes lRingPulse {
          0%{transform:scale(1);opacity:0.7}
          100%{transform:scale(1.9);opacity:0}
        }

        /* ── Particles ── */
        @keyframes lPart {
          0%  {opacity:1;transform:translate(-50%,-50%) translate(0,0) scale(1) rotate(0deg)}
          65% {opacity:0.7}
          100%{opacity:0;transform:translate(-50%,-50%) translate(var(--dx),var(--dy)) scale(0.08) rotate(var(--rot))}
        }

        /* ── Floating sparkles ── */
        @keyframes lSparkle {
          0%  {opacity:0;transform:translateY(0) translateX(0) scale(0.5)}
          18% {opacity:0.9}
          80% {opacity:0.5}
          100%{opacity:0;transform:translateY(-72vh) translateX(var(--drift)) scale(0.15)}
        }

        /* ── Crown drop ── */
        @keyframes lCrownDrop {
          from{transform:translateY(-22px) scale(0.4);opacity:0}
          to{transform:translateY(0) scale(1);opacity:1}
        }

        /* ── Sparkle icon ── */
        @keyframes lSpkIcon {
          0%,100%{opacity:0.35;transform:scale(0.75)}
          50%{opacity:1;transform:scale(1.35)}
        }
      `}</style>
    </div>
  );
}
